/**
 * Trois tapes brèves au même endroit, puis un maintien : la reconnaissance du
 * geste vit ici, en fonction pure, et le crochet React ne fait que lui porter
 * des évènements et armer une minuterie. C'est le partage qu'ont déjà les vols
 * de cartes — la géométrie dans `card-flight.ts`, le montage dans le composant
 * — et il vaut pour la même raison : un geste se règle au dixième de seconde,
 * et cela se teste sans navigateur.
 */
export interface PeekTiming {
  /** Délai maximal entre deux tapes pour qu'elles comptent ensemble. */
  tapWindow: number;
  /** Durée d'une tape : au-delà, ce n'est plus une tape mais un appui. */
  tapMaxDuration: number;
  /** Maintien à tenir, après la troisième tape, avant que ça ne s'ouvre. */
  holdDelay: number;
  /** Dérive du doigt tolérée, d'une tape à l'autre comme pendant l'appui. */
  moveTolerance: number;
}

export const DEFAULT_PEEK_TIMING: PeekTiming = {
  tapWindow: 420,
  tapMaxDuration: 260,
  holdDelay: 240,
  moveTolerance: 12,
};

/** Nombre de tapes qui arment le maintien. */
export const PEEK_TAPS = 3;

export interface PeekState {
  taps: number;
  lastTapEnd: number;
  pressedAt: number;
  origin: { x: number; y: number } | null;
  /** Pointeur qui porte le geste : un second doigt ne s'en mêle pas. */
  pointerId: number | null;
  /** Le compte est atteint et le doigt tient : une minuterie doit courir. */
  holding: boolean;
  /** Les cases sont retournées. */
  peeking: boolean;
}

export const IDLE_PEEK: PeekState = {
  taps: 0,
  lastTapEnd: 0,
  pressedAt: 0,
  origin: null,
  pointerId: null,
  holding: false,
  peeking: false,
};

export type PeekEvent =
  | { type: "down"; pointerId: number; x: number; y: number; at: number; emptyZone: boolean }
  | { type: "move"; pointerId: number; x: number; y: number }
  | { type: "up"; pointerId: number; at: number }
  /** La minuterie du maintien est arrivée à son terme. */
  | { type: "hold-elapsed" }
  | { type: "abandon" };

const distance = (from: { x: number; y: number }, x: number, y: number) =>
  Math.hypot(x - from.x, y - from.y);

export const reducePeek = (
  state: PeekState,
  event: PeekEvent,
  timing: PeekTiming = DEFAULT_PEEK_TIMING,
): PeekState => {
  switch (event.type) {
    case "abandon":
      return IDLE_PEEK;

    case "down": {
      // Un appui qui tombe sur ce qui se manipule, ou un second doigt, annule
      // le compte : la grille se tape sans arrêt pendant une partie, et le
      // geste ne doit jamais se confondre avec un coup joué.
      if (!event.emptyZone || state.pointerId !== null) return IDLE_PEEK;

      const drifted =
        state.origin !== null &&
        distance(state.origin, event.x, event.y) > timing.moveTolerance;
      // Une tape trop tardive, ou trop loin de la précédente, ouvre un compte
      // neuf plutôt que de prolonger celui d'avant.
      const continued = event.at - state.lastTapEnd <= timing.tapWindow && !drifted;
      const taps = (continued ? state.taps : 0) + 1;

      return {
        ...state,
        taps,
        pressedAt: event.at,
        origin: { x: event.x, y: event.y },
        pointerId: event.pointerId,
        holding: taps >= PEEK_TAPS,
        peeking: false,
      };
    }

    case "move": {
      if (state.pointerId !== event.pointerId || state.origin === null) return state;
      // Le doigt part en défilement : ce n'était pas ce geste-là.
      return distance(state.origin, event.x, event.y) > timing.moveTolerance ? IDLE_PEEK : state;
    }

    case "up": {
      if (state.pointerId !== event.pointerId) return state;
      // Le maintien s'achève : les cases se recachent, et le compte repart de
      // zéro pour que le geste doive être refait en entier.
      if (state.peeking) return IDLE_PEEK;
      // Un appui prolongé n'est pas une tape : il ne nourrit pas le compte.
      if (event.at - state.pressedAt > timing.tapMaxDuration) {
        return { ...IDLE_PEEK, origin: state.origin };
      }
      return { ...state, pointerId: null, holding: false, lastTapEnd: event.at };
    }

    case "hold-elapsed":
      return state.holding ? { ...state, holding: false, peeking: true } : state;
  }
};

/**
 * Une tape ne compte que si elle tombe à côté de tout ce qui se manipule. Sans
 * ce tri, trois tapes sur une carte armeraient le geste en même temps qu'elles
 * jouent le coup.
 */
const INTERACTIVE = 'button, a, input, select, textarea, label, [role="button"], [data-no-peek]';

export const isEmptyZone = (target: EventTarget | null): boolean =>
  target instanceof Element ? target.closest(INTERACTIVE) === null : false;
